/**
 * ReauthModal — W1.7
 *
 * Passwort-Bestaetigungs-Modal vor dem Claim.
 * Token wird ausschliesslich via useReauth-Ref verwaltet — niemals in Storage.
 *
 * AC-2: Titel, Passwort-Input, CTA, Loading-State, Fehler-Inline.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onSuccess: (reauthToken: string) => void
  onCancel: () => void
  authenticate: (password: string) => Promise<boolean>
  getToken: () => string | null
}

export function ReauthModal({ onSuccess, onCancel, authenticate, getToken }: Props) {
  const { t } = useTranslation('claims')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    const ok = await authenticate(password)
    setIsLoading(false)

    if (ok) {
      const token = getToken()
      if (token) {
        onSuccess(token)
      }
    } else {
      setError(t('reauthModal.errors.wrongPassword'))
      setPassword('')
    }
  }

  return (
    <div
      className="drawer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-modal-title"
    >
      <div className="card reauth-modal">
        <h2 id="reauth-modal-title">{t('reauthModal.title')}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label className="label" htmlFor="reauth-password">
              {t('reauthModal.passwordLabel')}
            </label>
            <input
              id="reauth-password"
              type="password"
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>

          {error && (
            <p className="inline-error" role="alert" aria-live="assertive">
              {error}
            </p>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || !password}
            >
              {isLoading ? t('reauthModal.actions.verifying') : t('common:actions.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
